<template>
  <div class="overall-stat">
    <div class="overall-stat-list">
      <div class="overall-stat-list-tip">
        展示数据为现存的学校、学生等数量，若学生或学校激活码过期等情况，则不包含在内。
        <span>现存数据从2022年6月23日后开始计算</span>
      </div>
      <div class="overall-stat-list-box">
        <StatList :list="statList" :loading="!loading" />
      </div>
    </div>
    <div class="overall-stat-charts">
      <div class="overall-stat-charts-left-box">
        <OverallStatLeft :regionId="regionId" :areaId="areaId" />
      </div>
      <div class="overall-stat-charts-right-box">
        <div v-loading="!loading" class="overall-stat-charts-right">
          <EchartBox
            routeName="OverallStat"
            v-if="loading"
            echartType="付费人数"
            echartTips="学生数按照开通激活码数量计算，部分激活码可能未交付学生使用"
            :areaId="areaId"
            :regionId="regionId"
            :getStatFunc="getPayStat"
            needFilterType="statType"
          />
        </div>
        <div v-loading="!loading" class="overall-stat-charts-right second-line">
          <SchoolStudentRank
            v-if="loading"
            :areaId="areaId"
            :regionId="regionId"
          />
        </div>
        <div v-loading="!loading" class="overall-stat-charts-right three-line">
          <EchartBox
            routeName="OverallStat"
            v-if="loading"
            echartType="付费占比（人次）"
            :boxHeight="520"
            :areaId="areaId"
            :regionId="regionId"
            :getStatFunc="getPayProportionStat"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { formatInc } from '@/utils/common'
import constant from '@/utils/constant'
import StatList from '../common/StatList'
import EchartBox from '../common/EchartBox'
import SchoolStudentRank from './SchoolStudentRank'
import OverallStatLeft from './OverallStatLeft'

export default {
  name: 'OverallStat',
  components: {
    EchartBox,
    StatList,
    SchoolStudentRank,
    OverallStatLeft,
  },
  props: {
    regionId: [String, Number],
    areaId: [String, Number],
    filterTimes: Object,
  },
  data() {
    return {
      loading: false,
      statList: [],
    }
  },
  computed: {
    statKeys() {
      return [
        {
          label: '学校总数',
          key: 'activeSchoolCount',
          footerKey: 'weeklyAggStuActiveCount',
          footerText: '本周活跃学生',
          pathname: 'School',
          tipText: '满足30个激活学生的学校数量',
        },
        {
          label: '校本学校总数',
          key: 'schoolCampusCount',
          footerKey: 'freeCampusStudentCount',
          footerText: '校本学生数',
          tipText: '满足30个学生开通激活码的学校数量',
          pathname: 'School',
          pathQuery: {
            type: 'isCampusActive',
          },
        },
        {
          label: '社团学校总数',
          key: 'school430Count',
          footerKey: 'pay430StudentCount',
          footerText: '社团学生数',
          tipText: '满足30个学生开通激活码的学校数量',
          pathname: 'School',
          pathQuery: {
            type: 'is430Active',
          },
        },
        {
          label: '付费激活码使用',
          key: 'pay430ActivateCodeTotal/payOrgActivateCodeTotal',
          pathname: 'PayDetail',
          pathQuery: {
            scrollTo: 'code',
          },
        },
        {
          label: '教师总数',
          key: 'teacherTotal',
          footerKey: 'scale',
          footerText: '老师 / 学生比例',
          scaleKey: 'activeStudentTotal',
        },
        {
          label: '机构总数',
          key: 'orgTotal',
          pathname: 'Org',
        },
        {
          label: '学生总数',
          key: 'activeStudentTotal',
        },
        {
          label: '作品总数',
          key: 'totalWorldCreationCount',
        },
      ]
    },
    filterParams() {
      return {
        regionId: this.regionId,
        ...this.filterTimes,
      }
    },
  },
  watch: {
    filterParams: {
      async handler(params) {
        if (params.regionId && params.unit) {
          this.loading = false
          const filterTimes = {
            ...params,
            unit: params.unit === 'all' ? 'day' : params.unit,
          }
          const regionStat = await this.onGetRegionStats(filterTimes)
          const lastMonthRegionStat = await this.getLastMonthRegionStat(
            this.regionId
          )
          const lastWeekRegionStat = await this.getLastWeekRegionStat(
            this.regionId
          )
          this.statList = this.getStatList(
            regionStat[0],
            lastMonthRegionStat[0],
            lastWeekRegionStat[0]
          )
          this.loading = true
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      getRegionStats: 'newStat/getRegionStats',
    }),
    getStatList(
      yesterdayRegionStat = {},
      lastMonthRegionStat = {},
      lastWeekRegionStat = {}
    ) {
      return this.statKeys.map(item => {
        let yesterdayTotal = 0
        let lastMonthTotal = 0
        let lastWeekTotal = 0
        if (item.key.includes('/')) {
          const [leftKey, rightKey] = item.key.split('/')
          yesterdayTotal =
            (yesterdayRegionStat[leftKey] || 0) +
            (yesterdayRegionStat[rightKey] || 0)
          lastMonthTotal =
            (lastMonthRegionStat[leftKey] || 0) +
            (lastMonthRegionStat[rightKey] || 0)
          lastWeekTotal =
            (lastWeekRegionStat[leftKey] || 0) +
            (lastWeekRegionStat[rightKey] || 0)
        } else {
          yesterdayTotal = yesterdayRegionStat[item.key] || 0
          lastMonthTotal = lastMonthRegionStat[item.key] || 0
          lastWeekTotal = lastWeekRegionStat[item.key] || 0
        }

        let { inc: monthInc, add: monthAdd } = formatInc(
          yesterdayTotal,
          lastMonthTotal
        )
        let { inc: weekInc, add: weekAdd } = formatInc(
          yesterdayTotal,
          lastWeekTotal
        )

        let statFooter = ''
        if (item.footerKey) {
          if (item.footerKey === 'scale') {
            const scaleTotal = yesterdayRegionStat[item.scaleKey] || 0
            let molecule = 0
            let denominator = 0
            if (yesterdayTotal > 0) {
              molecule = 1
              denominator =
                Math.floor((scaleTotal / yesterdayTotal) * 100) / 100
            }
            statFooter = `${item.footerText}  ${molecule}：${denominator}`
          } else if (item.footerKey.includes('/')) {
            const [leftKey, rightKey] = item.footerKey.split('/')
            statFooter = `${item.footerText}： ${
              (yesterdayRegionStat[leftKey] || 0) +
              (yesterdayRegionStat[rightKey] || 0)
            }`
          } else {
            statFooter = `${item.footerText}： ${
              yesterdayRegionStat[item.footerKey] || 0
            } 人`
          }
        }
        return {
          label: item.label,
          total: item.totalFunc
            ? item.totalFunc(
                yesterdayTotal,
                yesterdayRegionStat[item.denominatorKey]
              )
            : yesterdayTotal,
          monthInc,
          monthAdd,
          weekInc,
          weekAdd,
          statFooter,
          pathname: item.pathname,
          pathQuery: item.pathQuery,
          tipText: item.tipText,
        }
      })
    },
    async onGetRegionStats(params) {
      return await this.getRegionStats(params)
    },
    async getYesterdayRegionStat(regionId) {
      const yesterdayDate = moment().subtract(1, 'days').format('YYYY-MM-DD')
      return await this.getRegionStats({
        regionId,
        startTime: yesterdayDate,
        endTime: yesterdayDate,
      })
    },
    async getLastMonthRegionStat(regionId) {
      const lastMonthEndDate = moment()
        .subtract(1, 'months')
        .endOf('month')
        .format('YYYY-MM-DD')
      return await this.getRegionStats({
        regionId,
        unit: 'month',
        startTime: lastMonthEndDate,
        endTime: lastMonthEndDate,
      })
    },
    async getLastWeekRegionStat(regionId) {
      const lastWeekEndDate = moment()
        .subtract(1, 'weeks')
        .endOf('week')
        .add(1, 'days')
        .format('YYYY-MM-DD')
      return await this.getRegionStats({
        regionId,
        unit: 'week',
        startTime: lastWeekEndDate,
        endTime: lastWeekEndDate,
      })
    },
    async getPayStat({ regionId, type }) {
      const stats = await this.getYesterdayRegionStat(regionId)
      const stat = stats[0] || {}
      const xAxisData = ['免费学生数', '付费学生数']
      const seriesData = {
        type: 'pictorialBar',
        label: {
          normal: {
            show: true,
            position: 'top',
            textStyle: {
              fontSize: 16,
            },
          },
        },
        symbolRepeat: true,
        symbolSize: ['40%', '40%'],
        barCategoryGap: '10%',
        barMaxWidth: '20%',
        data: [
          {
            value: 0,
            symbol: constant.echartIconPeople,
            itemStyle: {
              color: '#38cfda',
            },
          },
          {
            value: 0,
            symbol: constant.echartIconPeople,
            itemStyle: {
              color: '#869CF4',
            },
          },
        ],
      }
      if (type === 'now') {
        seriesData.data[0].value = stat.freeStudentCount || 0
        seriesData.data[1].value = stat.payStudentCount || 0
      } else {
        seriesData.data[0].value = stat.freeStudentTotal || 0
        seriesData.data[1].value = stat.payStudentTotal || 0
      }
      return {
        echartEl: 'pay-stat',
        echartOptions: {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
          },
          grid: {
            left: '6%',
            right: '6%',
            bottom: '6%',
            containLabel: true,
          },
          xAxis: {
            data: xAxisData,
            inverse: true,
            axisTick: { show: false },
            axisLabel: {
              margin: 30,
              fontSize: 14,
            },
            axisPointer: {
              label: {
                show: true,
                margin: 30,
              },
            },
          },
          yAxis: {
            splitLine: { show: false },
            axisTick: { show: false },
            axisLine: { show: false },
            minInterval: 1,
          },
          series: seriesData,
        },
      }
    },
    async getPayProportionStat({ regionId }) {
      const stats = await this.getYesterdayRegionStat(regionId)
      const stat = stats[0] || {}
      const xAxisData = ['社团课', '机构课', '会员']
      const seriesData = [
        { value: stat.pay430StudentCount || 0, name: '社团课' },
        { value: stat.payOrgStudentCount || 0, name: '机构课' },
        { value: stat.payVipStudentCount || 0, name: '会员' },
      ]
      return {
        echartEl: 'pay-proportion-stat',
        echartOptions: {
          tooltip: {
            trigger: 'item',
            formatter: '{b}<br/>{c} ({d}%)',
          },
          legend: {
            bottom: '5%',
            data: xAxisData,
          },
          series: [
            {
              type: 'pie',
              center: ['50%', '45%'],
              radius: ['30%', '50%'],
              label: {
                normal: {
                  formatter: '{b}\n{d}%',
                },
              },
              data: seriesData,
            },
          ],
        },
      }
    },
  },
}
</script>

<style lang='scss'>
.overall-stat {
  margin-top: 60px;
  .overall-stat-list {
    background: #fff;
    padding: 90px 28px 28px;
    box-shadow: 0px 0px 10px 0px rgba(92, 92, 93, 0.1);
    border-radius: 4px;
    .overall-stat-list-tip {
      font-size: 14px;
      color: #999;
      span {
        color: #333;
      }
    }
  }
  .overall-stat-charts {
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    .overall-stat-charts-right {
      width: 100%;
      margin-bottom: 20px;
      height: 500px;
    }
    .overall-stat-charts-left-box {
      width: 60%;
      padding: 20px 20px 0px;
      box-sizing: border-box;
      background: #fff;
      box-shadow: 0px 0px 10px 0px rgba(92, 92, 93, 0.1);
      border-radius: 4px;
    }
    .overall-stat-charts-right-box {
      width: 38%;
    }
    .three-line {
      margin-bottom: 0px;
    }
  }
}
</style>